import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ShieldAlert, ShieldQuestion, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * ZatcaHealthWidget — iter805.
 * At-a-glance ZATCA e-invoice compliance for today: Reported/Cleared vs
 * Failed vs Pending, with a one-click "Retry all failed" bulk action.
 * Self-hides entirely for tenants that aren't onboarded to Phase 2.
 */
export default function ZatcaHealthWidget() {
  const [data, setData] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/zatca-p2/health-summary?scope=today');
      setData(res.data || null);
    } catch { setData(null); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Not onboarded, or fetch failed → render nothing (no dashboard noise).
  if (!data || !data.onboarded) return null;

  const c = data.counts || {};
  const ok = (c.REPORTED || 0) + (c.CLEARED || 0);
  const failed = c.FAILED || 0;
  const pending = c.PENDING || 0;
  const total = data.total || 0;

  const handleRetry = async () => {
    setRetrying(true);
    const tId = toast.loading('Retrying failed e-invoices…');
    try {
      const res = await api.post('/zatca-p2/retry-failed', {});
      toast.success(`Queued ${res.data.queued} invoice(s) for retry`, { id: tId });
      // Give the background submissions a moment, then refresh counts.
      setTimeout(load, 6000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed', { id: tId });
    } finally {
      setRetrying(false);
    }
  };

  const stat = (label, value, cls, Icon) => (
    <div className="flex items-center gap-2" data-testid={`zatca-health-${label.toLowerCase()}`}>
      <div className={`p-1.5 rounded-lg ${cls}`}><Icon size={15} /></div>
      <div className="leading-tight">
        <div className="text-lg font-bold tabular-nums">{value}</div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      </div>
    </div>
  );

  return (
    <Card
      className={failed > 0 ? 'border-red-300 bg-red-50/40' : 'border-emerald-200 bg-emerald-50/30'}
      data-testid="zatca-health-widget"
    >
      <CardContent className="p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2 mr-2">
          <FileText size={18} className="text-muted-foreground" />
          <div>
            <div className="text-sm font-bold">ZATCA e-Invoicing · Today</div>
            <div className="text-[10px] text-muted-foreground">
              {total} submitted · {data.environment || 'production'}
            </div>
          </div>
        </div>

        {stat('Reported', ok, 'bg-emerald-100 text-emerald-700', ShieldCheck)}
        {stat('Pending', pending, 'bg-amber-100 text-amber-700', ShieldQuestion)}
        {stat('Failed', failed, 'bg-red-100 text-red-700', ShieldAlert)}

        <div className="ml-auto">
          {failed > 0 ? (
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={retrying}
              onClick={handleRetry}
              data-testid="zatca-health-retry-all-btn"
            >
              <RefreshCw size={14} className={`mr-1.5 ${retrying ? 'animate-spin' : ''}`} />
              {retrying ? 'Retrying…' : `Retry ${failed} failed`}
            </Button>
          ) : (
            <span className="text-xs font-medium text-emerald-700 flex items-center gap-1" data-testid="zatca-health-all-clear">
              <ShieldCheck size={14} /> All compliant
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
